/**
 * Workshop pack B: eight courses from Rivet Alley through Workshop Finale.
 */

import { Terrain, type LevelData, type ObjectPlacement } from '../game/types'
import {
  blank,
  box,
  catwalk,
  coinArc,
  coins,
  courseShell,
  finish,
  floor,
  ledge,
  place,
  placeRow,
  pipe,
  scenery,
} from './paint'

/** Dense rivets, a shell alley, and three coin-traced gaps. */
function rivetAlley(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 34, 25)
  floor(g, 38, 68, 25)
  floor(g, 72, 106, 25)
  floor(g, 110, 159, 25)

  scenery(o, [
    [8, 8, 'cloud'],
    [48, 7, 'cloud2'],
    [88, 9, 'cloud'],
    [128, 8, 'cloud2'],
    [20, 23, 'hill'],
    [60, 24, 'bush'],
    [100, 23, 'hill'],
  ])

  place(o, 'expansion-cell', 8, 24)
  place(o, 'rivet', 12, 24)
  place(o, 'rivet', 16, 24)
  place(o, 'rivet', 20, 24)
  place(o, 'crate', 24, 21)
  place(o, 'crate', 26, 21)

  place(o, 'scout', 30, 24)
  place(o, 'brick', 32, 24)
  place(o, 'brick', 33, 24)
  coinArc(o, 35, 37, 24)

  place(o, 'rivet', 44, 24)
  place(o, 'rivet', 48, 24)
  place(o, 'rivet', 52, 24)
  place(o, 'scout', 58, 24)
  place(o, 'rivet', 64, 24)
  coins(o, 46, 54, 24)
  coinArc(o, 69, 71, 24)

  place(o, 'checkpoint', 76, 24)
  place(o, 'rivet', 82, 24)
  place(o, 'rivet', 86, 24)
  place(o, 'rivet', 90, 24)
  place(o, 'scout', 96, 24)
  place(o, 'brick', 98, 21)
  place(o, 'crate', 99, 21, { contains: 'spare-battery' })
  place(o, 'brick', 100, 21)
  coinArc(o, 107, 109, 24)

  place(o, 'rivet', 116, 24)
  place(o, 'rivet', 122, 24)
  place(o, 'rivet', 128, 24)
  place(o, 'rivet', 134, 24)
  place(o, 'prism', 140, 24)
  coins(o, 136, 142, 24)
  finish(o, 150, 25)

  return courseShell('Rivet Alley', 'overworld', 340, 3, 24, g, o)
}

/** Wheel scouts, wanderers, pipes, and a vertical lift to the flag terrace. */
function scoutCircuit(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 100, 25)
  floor(g, 120, 159, 25)

  scenery(o, [
    [12, 8, 'cloud'],
    [55, 7, 'cloud2'],
    [95, 9, 'cloud'],
    [30, 23, 'hill'],
    [70, 24, 'bush'],
  ])

  place(o, 'scout', 12, 24)
  place(o, 'crate-expansion-cell', 18, 21)
  pipe(o, 24, 23, 3)
  pipe(o, 32, 22, 4)
  coins(o, 24, 25, 21)
  coins(o, 32, 33, 20)

  place(o, 'wanderer', 42, 24)
  place(o, 'scout', 50, 24)
  place(o, 'brick', 52, 24)
  place(o, 'brick', 53, 24)
  place(o, 'rivet', 58, 24)
  place(o, 'scout', 66, 24)
  place(o, 'checkpoint', 72, 24)

  place(o, 'wanderer', 80, 24)
  place(o, 'scout', 88, 24)
  place(o, 'crate', 94, 21, { contains: 'ember' })
  place(o, 'scout', 98, 24)

  // Pit bridged by the lift landing chain.
  place(o, 'moving-platform', 106, 24, { range: 10, speed: 0.35 })
  placeRow(o, 'platform', 108, 14, 112)
  ledge(g, 114, 14, 119, 14, Terrain.HARD)
  place(o, 'scout', 116, 13)
  ledge(g, 120, 14, 159, 24, Terrain.GROUND)
  place(o, 'spare-battery', 126, 13)
  place(o, 'rivet', 134, 13)
  place(o, 'scout', 142, 13)
  coins(o, 128, 138, 13)
  finish(o, 150, 14)

  return courseShell('Scout Circuit', 'overworld', 330, 3, 24, g, o)
}

/** Sky catwalks with a mid-course shield prism. */
function prismBridge(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 18, 25)
  floor(g, 140, 159, 25)

  scenery(o, [
    [6, 6, 'cloud'],
    [40, 4, 'cloud2'],
    [80, 5, 'cloud'],
    [120, 4, 'cloud2'],
  ])

  place(o, 'rivet', 10, 24)
  place(o, 'crate-expansion-cell', 14, 21)
  placeRow(o, 'platform', 22, 24, 26)
  placeRow(o, 'platform', 30, 23, 34)
  placeRow(o, 'platform', 38, 22, 42)
  coins(o, 22, 25, 23)
  place(o, 'rivet', 32, 22)

  placeRow(o, 'platform', 46, 22, 50)
  place(o, 'spike', 48, 21)
  placeRow(o, 'platform', 54, 21, 60)
  place(o, 'prism', 56, 19)
  coins(o, 54, 58, 20)
  place(o, 'checkpoint', 59, 20)

  placeRow(o, 'platform', 64, 21, 68)
  place(o, 'rivet', 66, 20)
  placeRow(o, 'platform', 72, 20, 76)
  placeRow(o, 'platform', 80, 19, 84)
  place(o, 'scout', 82, 18)
  placeRow(o, 'platform', 88, 20, 92)
  place(o, 'spike', 90, 19)

  placeRow(o, 'platform', 96, 19, 100)
  placeRow(o, 'platform', 104, 18, 108)
  place(o, 'rivet', 106, 17)
  placeRow(o, 'platform', 112, 18, 116)
  placeRow(o, 'platform', 120, 17, 128)
  place(o, 'spare-battery', 122, 16)
  place(o, 'checkpoint', 126, 16)

  placeRow(o, 'platform', 132, 18, 136)
  placeRow(o, 'platform', 138, 20, 141)
  placeRow(o, 'platform', 142, 22, 144)
  floor(g, 145, 159, 25)
  place(o, 'rivet', 148, 24)
  coins(o, 146, 152, 24)
  finish(o, 152, 25)

  return courseShell('Prism Bridge', 'sky', 360, 3, 24, g, o)
}

/** Spring tiers, a shuttle, and a high pier finish. */
function cloudPier(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 20, 25)
  floor(g, 145, 159, 25)

  scenery(o, [
    [8, 5, 'cloud'],
    [50, 3, 'cloud2'],
    [90, 4, 'cloud'],
    [130, 3, 'cloud2'],
  ])

  place(o, 'rivet', 8, 24)
  place(o, 'crate-expansion-cell', 14, 21)
  place(o, 'spring', 18, 24)
  placeRow(o, 'platform', 20, 18, 26)
  coins(o, 21, 25, 17)

  placeRow(o, 'platform', 30, 18, 34)
  place(o, 'rivet', 32, 17)
  placeRow(o, 'platform', 38, 17, 42)
  place(o, 'moving-platform-h', 46, 17, { range: 4, speed: 0.4 })
  placeRow(o, 'platform', 54, 17, 58)
  place(o, 'checkpoint', 56, 16)

  place(o, 'spring', 60, 16)
  placeRow(o, 'platform', 62, 12, 68)
  coins(o, 63, 67, 11)
  place(o, 'scout', 66, 11)
  placeRow(o, 'platform', 72, 12, 76)
  placeRow(o, 'platform', 80, 13, 84)
  place(o, 'rivet', 82, 12)
  placeRow(o, 'platform', 88, 12, 92)
  place(o, 'prism', 90, 10)

  placeRow(o, 'platform', 96, 13, 100)
  placeRow(o, 'platform', 104, 14, 108)
  place(o, 'checkpoint', 106, 13)
  placeRow(o, 'platform', 112, 14, 116)
  place(o, 'spring', 118, 14)
  placeRow(o, 'platform', 120, 10, 126)
  coins(o, 121, 125, 9)
  place(o, 'spare-battery', 124, 9)

  placeRow(o, 'platform', 130, 12, 134)
  placeRow(o, 'platform', 136, 14, 140)
  placeRow(o, 'platform', 142, 18, 144)
  ledge(g, 145, 18, 159, 24, Terrain.GROUND)
  place(o, 'rivet', 148, 17)
  finish(o, 152, 18)

  return courseShell('Cloud Pier', 'sky', 350, 3, 24, g, o)
}

/** Slippery ice runs over lava with hard-block safe steps. */
function iceworks(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 16, 26, Terrain.HARD)
  floor(g, 140, 159, 26, Terrain.HARD)

  place(o, 'rivet', 6, 25)
  place(o, 'crate-expansion-cell', 10, 22)
  placeRow(o, 'ice-block', 18, 25, 28)
  placeRow(o, 'lava', 18, 28, 48)
  ledge(g, 30, 24, 33, 26, Terrain.HARD)
  place(o, 'spike', 31, 23)
  placeRow(o, 'ice-block', 36, 25, 46)
  ledge(g, 48, 24, 54, 26, Terrain.HARD)
  place(o, 'checkpoint', 50, 23)
  place(o, 'spare-battery', 52, 23)

  placeRow(o, 'lava', 55, 28, 90)
  placeRow(o, 'ice-block', 56, 24, 64)
  place(o, 'rivet', 60, 23)
  ledge(g, 68, 23, 71, 26, Terrain.HARD)
  placeRow(o, 'ice-block', 74, 24, 84)
  place(o, 'shardling', 78, 23)
  ledge(g, 88, 23, 94, 26, Terrain.HARD)
  place(o, 'checkpoint', 90, 22)
  place(o, 'prism', 92, 22)

  placeRow(o, 'lava', 95, 28, 139)
  placeRow(o, 'ice-block', 96, 24, 108)
  place(o, 'saw-h', 100, 23, { range: 4, speed: 0.35 })
  ledge(g, 112, 23, 116, 26, Terrain.HARD)
  placeRow(o, 'ice-block', 118, 24, 130)
  place(o, 'rivet', 124, 23)
  ledge(g, 132, 22, 138, 26, Terrain.HARD)
  place(o, 'spare-battery', 136, 21)

  place(o, 'rivet', 144, 25)
  coins(o, 146, 150, 25)
  finish(o, 152, 26)

  return courseShell('Iceworks', 'castle', 320, 3, 25, g, o)
}

/** Vertical and horizontal rotors with fair ledges and two checkpoints. */
function rotorRun(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 14, 26, Terrain.HARD)
  floor(g, 145, 159, 26, Terrain.HARD)
  placeRow(o, 'lava', 15, 28, 144)

  place(o, 'crate-expansion-cell', 6, 22)
  place(o, 'rivet', 10, 25)

  ledge(g, 16, 24, 20, 26, Terrain.HARD)
  place(o, 'saw', 18, 20, { range: 3, speed: 0.35 })
  ledge(g, 24, 23, 28, 26, Terrain.HARD)
  place(o, 'rivet', 26, 22)
  ledge(g, 32, 24, 36, 26, Terrain.HARD)
  place(o, 'saw-h', 33, 23, { range: 4, speed: 0.4 })
  ledge(g, 40, 23, 45, 26, Terrain.HARD)
  place(o, 'checkpoint', 42, 22)
  place(o, 'prism', 44, 22)

  ledge(g, 50, 24, 54, 26, Terrain.HARD)
  place(o, 'shardling', 52, 23)
  ledge(g, 58, 22, 62, 26, Terrain.HARD)
  place(o, 'saw', 60, 18, { range: 4, speed: 0.35 })
  ledge(g, 66, 23, 70, 26, Terrain.HARD)
  place(o, 'rivet', 68, 22)
  ledge(g, 74, 24, 78, 26, Terrain.HARD)
  place(o, 'saw-h', 75, 23, { range: 5, speed: 0.4 })
  ledge(g, 82, 23, 88, 26, Terrain.HARD)
  place(o, 'checkpoint', 84, 22)
  place(o, 'spare-battery', 86, 22)

  ledge(g, 92, 24, 96, 26, Terrain.HARD)
  place(o, 'shardling', 94, 23)
  ledge(g, 100, 22, 104, 26, Terrain.HARD)
  place(o, 'saw', 102, 18, { range: 3, speed: 0.4 })
  ledge(g, 108, 23, 112, 26, Terrain.HARD)
  place(o, 'rivet', 110, 22)
  ledge(g, 116, 24, 120, 26, Terrain.HARD)
  place(o, 'saw-h', 117, 23, { range: 4, speed: 0.35 })
  ledge(g, 124, 22, 130, 26, Terrain.HARD)
  place(o, 'prism', 126, 21)
  ledge(g, 134, 23, 140, 26, Terrain.HARD)
  place(o, 'rivet', 136, 22)

  place(o, 'rivet', 148, 25)
  coins(o, 150, 154, 25)
  finish(o, 152, 26)

  return courseShell('Rotor Run', 'castle', 300, 3, 25, g, o)
}

/** Vertical underground climb to a high observatory flag. */
function vaultAscent(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  box(g, 0, 0, 159, 2, Terrain.GROUND)
  floor(g, 0, 28, 25)

  place(o, 'rivet', 8, 24)
  place(o, 'crate-expansion-cell', 14, 21)
  place(o, 'spring', 22, 24)
  placeRow(o, 'platform', 24, 18, 32)
  coins(o, 25, 31, 17)
  place(o, 'wisp', 30, 14)

  const climb: Array<[number, number, number]> = [
    [36, 18, 42],
    [44, 16, 50],
    [36, 14, 42],
    [44, 12, 50],
    [36, 10, 42],
    [44, 8, 50],
    [52, 8, 64],
  ]
  for (const [x0, ty, x1] of climb) {
    catwalk(g, x0, ty, x1)
    coins(o, x0 + 1, x1 - 1, ty - 1)
  }
  place(o, 'wisp', 48, 12)
  place(o, 'checkpoint', 56, 7)
  place(o, 'spare-battery', 62, 7)

  // Lift carries you from the floor up onto the high run.
  place(o, 'moving-platform', 70, 24, { range: 14, speed: 0.35 })
  placeRow(o, 'platform', 72, 10, 78)
  place(o, 'prism', 74, 8)
  place(o, 'checkpoint', 76, 9)

  placeRow(o, 'platform', 82, 10, 88)
  place(o, 'wisp', 86, 6)
  placeRow(o, 'platform', 92, 10, 98)
  place(o, 'rivet', 94, 9)
  placeRow(o, 'platform', 102, 10, 108)
  place(o, 'scout', 104, 9)
  placeRow(o, 'platform', 112, 10, 118)
  coins(o, 113, 117, 9)

  // Continuous hard terrace into the flag — no void drops.
  ledge(g, 120, 10, 159, 24, Terrain.GROUND)
  place(o, 'rivet', 130, 9)
  place(o, 'spare-battery', 140, 9)
  coins(o, 132, 142, 9)
  finish(o, 150, 10)

  return courseShell('Vault Ascent', 'underground', 360, 3, 24, g, o)
}

/** Capstone: pipes, spring climb, lift, prism, shardling — hard but fair. */
function workshopFinale(): LevelData {
  const g = blank()
  const o: ObjectPlacement[] = []

  floor(g, 0, 36, 25)
  floor(g, 50, 80, 25)
  floor(g, 130, 159, 25)

  scenery(o, [
    [10, 7, 'cloud'],
    [60, 5, 'cloud2'],
    [110, 6, 'cloud'],
  ])

  place(o, 'rivet', 8, 24)
  place(o, 'crate-expansion-cell', 14, 21)
  pipe(o, 20, 23, 3)
  pipe(o, 26, 22, 4)
  pipe(o, 32, 21, 5)
  coins(o, 20, 21, 21)
  coins(o, 26, 27, 20)
  coins(o, 32, 33, 19)

  place(o, 'scout', 40, 24)
  place(o, 'brick', 42, 24)
  place(o, 'brick', 43, 24)
  place(o, 'moving-platform-h', 38, 23, { range: 4, speed: 0.45 })
  placeRow(o, 'platform', 46, 23, 49)
  coinArc(o, 37, 49, 25)
  place(o, 'checkpoint', 54, 24)

  place(o, 'spring', 62, 24)
  placeRow(o, 'platform', 64, 18, 72)
  place(o, 'rivet', 68, 17)
  placeRow(o, 'platform', 74, 16, 80)
  place(o, 'prism', 76, 14)
  place(o, 'shardling', 78, 15)

  placeRow(o, 'lava', 81, 27, 129)
  catwalk(g, 84, 22, 86)
  catwalk(g, 90, 21, 92)
  catwalk(g, 96, 20, 98)
  place(o, 'saw', 97, 16, { range: 3, speed: 0.35 })
  catwalk(g, 102, 20, 104)
  catwalk(g, 108, 19, 110)
  place(o, 'checkpoint', 108, 18)
  catwalk(g, 114, 19, 116)
  catwalk(g, 120, 18, 122)
  place(o, 'shardling', 120, 17)
  catwalk(g, 126, 18, 128)

  place(o, 'moving-platform', 132, 24, { range: 10, speed: 0.35 })
  placeRow(o, 'platform', 134, 14, 138)
  ledge(g, 140, 14, 159, 24, Terrain.GROUND)
  place(o, 'spare-battery', 144, 13)
  place(o, 'rivet', 148, 13)
  coins(o, 142, 150, 13)
  finish(o, 152, 14)

  return courseShell('Workshop Finale', 'castle', 400, 3, 24, g, o)
}

const factories = [
  rivetAlley,
  scoutCircuit,
  prismBridge,
  cloudPier,
  iceworks,
  rotorRun,
  vaultAscent,
  workshopFinale,
] as const

export const WORKSHOP_B_LIST: LevelData[] = factories.map((f) => f())

export const WORKSHOP_B_COURSES: Record<string, LevelData> = Object.fromEntries(
  WORKSHOP_B_LIST.map((c) => [c.name, c]),
)
